import { useQuery } from "@tanstack/react-query";
import { getApiClient } from "../auth/http.js";
import { useSession } from "../session/SessionContext.js";
import {
  getDemoCompany,
  getDemoDepartment,
  getDemoDepartmentTree,
  getDemoPosition,
  listDemoCompanies,
  listDemoDepartments,
  listDemoPositions,
} from "./demo-org.js";
import { queryKeys } from "./query-keys.js";

export function useCompanies() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.org.companies(),
    queryFn: () => (isDemoMode ? listDemoCompanies() : getApiClient().listCompanies()),
  });
}

export function useCompany(id: string | undefined) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.org.company(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) throw new Error("id obrigatório");
      return isDemoMode ? getDemoCompany(id) : getApiClient().getCompany(id);
    },
  });
}

export function useDepartments(companyId?: string) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.org.departments(companyId),
    queryFn: () =>
      isDemoMode
        ? listDemoDepartments(companyId)
        : getApiClient().listDepartments(companyId),
  });
}

export function useDepartmentTree(companyId?: string) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.org.departmentTree(companyId),
    queryFn: () =>
      isDemoMode
        ? getDemoDepartmentTree(companyId)
        : getApiClient().getDepartmentTree(companyId),
  });
}

export function useDepartment(id: string | undefined) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.org.department(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) throw new Error("id obrigatório");
      return isDemoMode ? getDemoDepartment(id) : getApiClient().getDepartment(id);
    },
  });
}

export function usePositions() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.org.positions(),
    queryFn: () => (isDemoMode ? listDemoPositions() : getApiClient().listPositions()),
  });
}

export function usePosition(id: string | undefined) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.org.position(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) throw new Error("id obrigatório");
      return isDemoMode ? getDemoPosition(id) : getApiClient().getPosition(id);
    },
  });
}
